/**
 * agent-ops-console ApplicationProfile (8.F placeholder).
 *
 * Second proof profile. Demonstrates that the same substrate binary
 * can boot a governance-first product without ivyx UI modules.
 * UI content is intentionally empty — the product's shell UI lives
 * in an Agent Ops extension (host territory, not substrate scope).
 *
 * Constitutional proof: when this profile and `ivyx-classic` both
 * boot from the same binary, the "two products, one substrate" claim
 * is mathematically demonstrated (ApplicationProfile constitutional closure).
 */

import type { shell } from 'punica';

type ApplicationProfile = shell.Profile.ApplicationProfile;

export const AGENT_OPS_PROFILE_ID = 'agent-ops-console';

/**
 * Minimal dashboard layout — no sidebar panels, content-centered.
 * The host's Agent Ops extension renders its UI into the content slot.
 */
const AGENT_OPS_LAYOUT_ROOT: shell.Layout.LayoutNode = {
  type: 'column',
  sizes: [100],
  children: [{ type: 'slot', id: 'content', role: 'content' }]
};

/**
 * Governance-safe capability allowlist.
 * Only read/audit/catalog capabilities permitted by default.
 * Substrate mutating capabilities (workspace write, secrets write, etc.)
 * are explicitly denied.
 */
const AGENT_OPS_ALLOWED_CAPABILITIES = [
  // Capability registry surface (read-only)
  'capability.catalog',
  'capability.schemaExport',
  // Audit / event surface (read-only)
  'event.query',
  'event.replay',
  // LLM read-only surface
  'llm.listProviders',
  // Workspace read-only
  'workspace.get',
  'fs.read',
  'fs.stat',
  'fs.exists',
  'fs.list',
  'fs.snapshot',
  // Approval / delegation (governance)
  'approval.grant',
  'approval.revoke',
  'delegation.create',
  // Flow read-only
  'flow.validate',
  // Cost reporting
  'cost.report'
];

/**
 * Capabilities explicitly blocked even if added to allowedIds later.
 */
const AGENT_OPS_DENIED_CAPABILITIES = [
  'fs.write',
  'fs.delete',
  'fs.copy',
  'fs.migrate',
  'fs.restore',
  'workspace.create',
  'workspace.delete',
  'secrets.set',
  'secrets.delete',
  'vcs.commit',
  'vcs.push'
];

export const agentOpsConsoleProfile: ApplicationProfile = {
  schemaVersion: '1',

  identity: {
    id: AGENT_OPS_PROFILE_ID,
    name: 'Agent Ops Console',
    version: '1.0.0',
    audience: 'compliance-officer',
    branding: {
      productName: 'Agent Ops Console'
    }
  },

  shell: {
    // No ActivityBar, no Flow shell — governance console only.
    // View module included for potential side-panel audit views.
    activeModules: [
      'shell.statusbar',
      'shell.view',
      'shell.layout',
      // ViewRegistry depends on shell.layout — keep it after layout.
      'shell.views',
      'shell.contentTabs'
    ],
    layout: {
      id: 'agent-ops-dashboard',
      root: AGENT_OPS_LAYOUT_ROOT
    }
  },

  theme: {
    mode: 'dark',
    density: 'compact',
    branding: {
      productName: 'Agent Ops Console'
    }
  },

  capabilities: {
    allowedIds: AGENT_OPS_ALLOWED_CAPABILITIES,
    deniedIds: AGENT_OPS_DENIED_CAPABILITIES,
    // Only core and extension capabilities by default.
    // ivy-node capabilities not permitted in governance console.
    allowedOrigins: ['core', 'extension', 'instruction-class']
  }
};
